import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import PersonalIdentityCard from '@/components/profile/PersonalIdentityCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { getUserErrorMessage } from '@/lib/error-messages';
import { CalendarDays, Crown, EyeIcon, EyeOffIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const API_BASE_URL = `${window.location.origin}/api`;

type Purchase = {
    id: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    updated_at: string;
};

const Settings = () => {
    const { user, token } = useAuth();

    // Billing history state
    const [billingHistory, setBillingHistory] = useState<Purchase[]>([]);
    const [isBillingLoading, setIsBillingLoading] = useState(false);
    const [billingError, setBillingError] = useState<string | null>(null);

    // Fetch billing history
    const fetchBillingHistory = async () => {
        if (!token) return;
        setIsBillingLoading(true);
        setBillingError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/purchases/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch billing history');
            const data = await response.json();
            let filtered = (data.purchases || []).filter((p: Purchase) =>
                ["COMPLETED", "PENDING", "FAILED"].includes(p.status)
            );
            filtered.sort((a: Purchase, b: Purchase) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setBillingHistory(filtered);
        } catch (err) {
            setBillingError('Could not load billing history.');
        } finally {
            setIsBillingLoading(false);
        }
    };

    // Only fetch when tab is rendered (or on mount if you prefer)
    useEffect(() => {
        fetchBillingHistory();
        // eslint-disable-next-line
    }, []);

    // Password form state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    // Loading states
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    // Show/hide password fields
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            toast.error('You must be logged in to change your password');
            return;
        }

        // Validation
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (passwordForm.newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        try {
            setIsUpdatingPassword(true);

            // Make API call to change password
            const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_password: passwordForm.currentPassword,
                    new_password: passwordForm.newPassword
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    getUserErrorMessage(
                        errorData?.detail,
                        'Unable to change password right now. Please try again later.'
                    )
                );
            }

            // Reset password form
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });

            toast.success('Password changed successfully');
        } catch (error) {
            toast.error(
                getUserErrorMessage(
                    error,
                    'Unable to change password right now. Please try again later.'
                )
            );
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    return (
        <DashboardLayout>
            <div className="space-y-8">
                <div>
                    <h1 className="text-3xl font-bold">Settings</h1>
                    <p className="text-gray-600 mt-1">
                        Manage your account settings and preferences
                    </p>
                </div>

                <Tabs defaultValue="profile">
                    <TabsList className="grid w-full grid-cols-4 mb-6">
                        <TabsTrigger value="profile">Profile</TabsTrigger>
                        <TabsTrigger value="password">Password</TabsTrigger>
                        <TabsTrigger value="billing">Subscription and Billing</TabsTrigger>
                    </TabsList>

                    <TabsContent value="profile">
                        <PersonalIdentityCard />
                    </TabsContent>

                    <TabsContent value="password">
                        <Card>
                            <form onSubmit={handlePasswordSubmit}>
                                <CardHeader>
                                    <CardTitle>Change Password</CardTitle>
                                    <CardDescription>
                                        Update your account password
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="currentPassword">Current Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="currentPassword"
                                                name="currentPassword"
                                                type={showCurrentPassword ? "text" : "password"}
                                                value={passwordForm.currentPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Enter your current password"
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            >
                                                {showCurrentPassword ?
                                                    <EyeOffIcon className="h-5 w-5 text-gray-500" /> :
                                                    <EyeIcon className="h-5 w-5 text-gray-500" />}
                                            </button>
                                        </div>
                                    </div>

                                    <Separator className="my-2" />

                                    <div className="space-y-2">
                                        <Label htmlFor="newPassword">New Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="newPassword"
                                                name="newPassword"
                                                type={showNewPassword ? "text" : "password"}
                                                value={passwordForm.newPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Enter new password"
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ?
                                                    <EyeOffIcon className="h-5 w-5 text-gray-500" /> :
                                                    <EyeIcon className="h-5 w-5 text-gray-500" />}
                                            </button>
                                        </div>
                                        <p className="text-sm text-gray-500">
                                            Password must be at least 8 characters long
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={passwordForm.confirmPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Confirm new password"
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ?
                                                    <EyeOffIcon className="h-5 w-5 text-gray-500" /> :
                                                    <EyeIcon className="h-5 w-5 text-gray-500" />}
                                            </button>
                                        </div>
                                    </div>
                                </CardContent>
                                <CardFooter className="flex justify-end border-t pt-6">
                                    <Button type="submit" disabled={isUpdatingPassword}>
                                        {isUpdatingPassword ? 'Changing Password...' : 'Change Password'}
                                    </Button>
                                </CardFooter>
                            </form>
                        </Card>
                    </TabsContent>

                    <TabsContent value="billing">
                        <Card>
                            <CardHeader>
                                <CardTitle>Subscription</CardTitle>
                                <CardDescription>View your current subscription status and manage your plan.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Status row */}
                                <div className="rounded-2xl border bg-gradient-to-br from-indigo-50 to-purple-50 p-5 md:p-6">
                                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5 rounded-full bg-indigo-100 p-2 text-indigo-700">
                                                <Crown className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Subscription</div>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <div className="text-2xl font-semibold">
                                                        {user?.subscription_mode === 'premium' ? 'Premium' : 'Free'}
                                                    </div>
                                                    <span className={`${user?.subscription_mode === 'premium' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'} inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                                                        {user?.subscription_mode === 'premium' ? 'Active' : 'Basic'}
                                                    </span>
                                                </div>
                                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                                                    <CalendarDays className="h-4 w-4 text-gray-500" />
                                                    {user?.subscription_mode === 'premium' && user?.subscription_expires_at ? (
                                                        <span>Expires on {new Date(user.subscription_expires_at).toLocaleDateString()}</span>
                                                    ) : (
                                                        <span>{user?.subscription_mode === 'premium' ? 'Active subscription' : 'Upgrade for full access'}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <a href="/subscription">
                                                <Button size="lg" className="shadow-sm">Manage subscription</Button>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <br/>
                        <Card>
                            <CardHeader>
                                <CardTitle>Billing History</CardTitle>
                                <CardDescription>See your previous purchases and their status.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {isBillingLoading ? (
                                    <div>Loading billing history...</div>
                                ) : billingError ? (
                                    <div className="text-red-500">{billingError}</div>
                                ) : billingHistory.length === 0 ? (
                                    <div>No billing history found.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead>
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {billingHistory.map((purchase) => (
                                                    <tr key={purchase.id}>
                                                        <td className="px-4 py-2 whitespace-nowrap">{new Date(purchase.created_at).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap">${purchase.amount.toFixed(2)}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap">
                                                            <span className={
                                                                purchase.status === 'COMPLETED'
                                                                    ? 'text-green-600 font-semibold'
                                                                    : purchase.status === 'PENDING'
                                                                        ? 'text-yellow-600 font-semibold'
                                                                        : 'text-red-600 font-semibold'
                                                            }>
                                                                {purchase.status === 'COMPLETED' ? 'Paid' : (purchase.status.charAt(0) + purchase.status.slice(1).toLowerCase())}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};

export default Settings;
